import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  Home, Wallet, ArrowUpCircle, ArrowDownCircle, History, Settings, 
  Bell, Copy, ChevronDown, RefreshCw, QrCode, Shield, HelpCircle, LogOut
} from "lucide-react";

const CryptoIcon = ({ symbol, size = "w-8 h-8" }) => {
  const icons = {
    BTC: { bg: '#F7931A', text: '₿' },
    ETH: { bg: '#627EEA', text: 'Ξ' },
    USDT: { bg: '#26A17B', text: 'T' },
    RUB: { bg: '#4A5568', text: '₽' },
  };
  const icon = icons[symbol] || { bg: '#666', text: '?' };
  return (
    <div className={`${size} rounded-full flex items-center justify-center text-white font-bold text-sm`} style={{ backgroundColor: icon.bg }}>
      {icon.text}
    </div>
  );
};

export default function CoinPayMain() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [browserTheme, setBrowserTheme] = useState('light');
  const [showBrowser, setShowBrowser] = useState(true);

  useEffect(() => {
    const dataParam = searchParams.get('data');
    const themeParam = searchParams.get('theme');
    const browserParam = searchParams.get('browser');
    
    if (themeParam) setBrowserTheme(themeParam);
    if (browserParam === 'false') setShowBrowser(false);
    
    if (dataParam) {
      try {
        const parsedData = JSON.parse(decodeURIComponent(dataParam));
        setData(parsedData);
        // Also check showBrowser from data
        if (parsedData.showBrowser === false) setShowBrowser(false);
        if (parsedData.browserTheme) setBrowserTheme(parsedData.browserTheme);
      } catch (e) {
        console.error("Error parsing data:", e);
      }
    }
    document.title = 'Кошелёк | CoinPay';
    
    // Set body background to match content (fixes zoom issue)
    document.body.style.backgroundColor = '#f8fafc';
    document.documentElement.style.backgroundColor = '#f8fafc';
    
    let favicon = document.querySelector("link[rel='icon']");
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }
    favicon.href = '/coinpay-favicon.svg';
    
    return () => {
      // Cleanup
      document.body.style.backgroundColor = '';
      document.documentElement.style.backgroundColor = '';
    };
  }, [searchParams]);

  if (!data) {
    return <div className="min-h-screen bg-gray-100 flex items-center justify-center">Загрузка...</div>;
  }

  const isDark = browserTheme === 'dark';
  const theme = {
    bg: isDark ? '#202124' : '#dee1e6',
    tabBarBg: isDark ? '#202124' : '#dee1e6',
    activeTabBg: isDark ? '#292a2d' : '#ffffff',
    inactiveTabBg: isDark ? '#35363a' : '#c9cdd4',
    addressBarBg: isDark ? '#292a2d' : '#ffffff',
    addressInputBg: isDark ? '#202124' : '#f1f3f4',
    textPrimary: isDark ? '#e8eaed' : '#202124',
    textSecondary: isDark ? '#9aa0a6' : '#5f6368',
    border: isDark ? '#3c4043' : '#dadce0',
  };

  const accountName = `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Пользователь';
  const userId = data.visibleId || '000000';

  const menuItems = [
    { icon: Home, label: 'Главная', active: true },
    { icon: Wallet, label: 'Кошелёк', active: false },
    { icon: ArrowUpCircle, label: 'Пополнить', active: false },
    { icon: ArrowDownCircle, label: 'Вывести', active: false },
    { icon: History, label: 'История', active: false },
    { icon: QrCode, label: 'QR-код', active: false },
    { icon: Shield, label: 'Безопасность', active: false },
    { icon: Settings, label: 'Настройки', active: false },
  ];

  const bottomItems = [
    { icon: HelpCircle, label: 'Поддержка' },
    { icon: LogOut, label: 'Выйти' },
  ];

  const cryptoBalances = [
    { symbol: 'USDT', name: 'Tether', balance: data.usdtBalance || '0.00', rub: (parseFloat(data.usdtBalance || 0) * 92).toFixed(2) },
    { symbol: 'BTC', name: 'Bitcoin', balance: data.btcBalance || '0.000000', rub: (parseFloat(data.btcBalance || 0) * 6500000).toFixed(2) },
    { symbol: 'ETH', name: 'Ethereum', balance: data.ethBalance || '0.0000', rub: (parseFloat(data.ethBalance || 0) * 240000).toFixed(2) },
    { symbol: 'RUB', name: 'Рубли', balance: data.balance || '0.00', rub: data.balance || '0.00' },
  ];

  const totalRub = cryptoBalances.reduce((sum, c) => sum + parseFloat(c.rub || 0), 0);

  const BrowserFrame = () => (
    <div style={{ backgroundColor: theme.tabBarBg }} className="flex-shrink-0">
      <div className="flex items-center h-[38px]" style={{ backgroundColor: theme.tabBarBg }}>
        <div className="flex items-end flex-1 pl-2">
          <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px] cursor-pointer" style={{ backgroundColor: theme.inactiveTabBg }}>
            <img src="/ozon-favicon.ico" alt="" className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-[13px] truncate flex-1" style={{ color: theme.textSecondary }}>Ozon Карта</span>
          </div>
          <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px] cursor-pointer" style={{ backgroundColor: theme.activeTabBg }}>
            <img src="/coinpay-favicon.svg" alt="" className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-[13px] truncate flex-1" style={{ color: theme.textPrimary }}>Кошелёк | CoinPay</span>
          </div>
          <button className="w-7 h-7 flex items-center justify-center rounded-full ml-1 mb-1" style={{ color: theme.textSecondary }}>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
          </button>
        </div>
        <div className="flex items-center h-full">
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#3c3d41]" style={{ color: theme.textSecondary }}><svg className="w-[10px] h-[1px]" viewBox="0 0 10 1"><rect fill="currentColor" width="10" height="1"/></svg></button>
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#3c3d41]" style={{ color: theme.textSecondary }}><svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1"><rect x="0.5" y="0.5" width="9" height="9"/></svg></button>
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#e81123] hover:text-white" style={{ color: theme.textSecondary }}><svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="1.2"><line x1="0" y1="0" x2="10" y2="10"/><line x1="10" y1="0" x2="0" y2="10"/></svg></button>
        </div>
      </div>
      <div className="flex items-center h-[40px] px-2" style={{ backgroundColor: theme.addressBarBg }}>
        <div className="flex items-center gap-1 mr-2">
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: isDark ? '#5f6368' : '#dadce0' }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg></button>
        </div>
        <div className="flex-1 flex items-center h-[32px] px-4 rounded-full" style={{ backgroundColor: theme.addressInputBg, border: `1px solid ${theme.border}` }}>
          <svg className="w-4 h-4 mr-3 flex-shrink-0" style={{ color: theme.textSecondary }} viewBox="0 0 24 24" fill="currentColor"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>
          <span className="text-[14px]" style={{ color: theme.textPrimary }}>coinpay.ru</span>
        </div>
        <div className="flex items-center gap-1 ml-3">
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="2"/><circle cx="12" cy="5" r="2"/><circle cx="12" cy="19" r="2"/></svg></button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-screen flex flex-col" style={{ backgroundColor: showBrowser ? theme.bg : '#f8fafc', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', overflow: 'hidden' }}>
      {showBrowser && <BrowserFrame />}
      
      <div className="flex-1 flex min-h-0" style={{ backgroundColor: '#f8fafc' }}>
        {/* Sidebar */}
        <aside className="w-56 bg-gradient-to-b from-[#1a1a2e] to-[#16213e] flex flex-col flex-shrink-0">
          <div className="p-4 flex items-center gap-2">
            <img src="/coinpay-logo-white.svg" alt="CoinPay" className="h-9 w-auto" style={{ maxWidth: '180px' }} />
          </div>

          <nav className="flex-1 py-2">
            {menuItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <a key={index} href="#" className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${item.active ? 'text-[#FFD700] bg-white/10' : 'text-gray-300 hover:bg-white/5'}`}>
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span>{item.label}</span>
                </a>
              );
            })}
            
            <div className="mt-4 border-t border-white/10 pt-2">
              {bottomItems.map((item, index) => {
                const Icon = item.icon;
                return (
                  <a key={index} href="#" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-400 hover:bg-white/5">
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span>{item.label}</span>
                  </a>
                );
              })}
            </div>
          </nav>
        </aside>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
            <h1 className="text-xl font-semibold text-gray-900">Кошелёк</h1>
            <div className="flex items-center gap-4">
              <button className="p-2 text-gray-500 hover:text-gray-700 relative">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">{accountName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}</span>
                </div>
                <span className="text-sm font-medium">{accountName}</span>
                <span className="text-xs text-gray-500">ID: {userId}</span>
                <Copy className="w-4 h-4 text-gray-400 cursor-pointer" />
              </div>
            </div>
          </header>

          {/* Content */}
          <main className="flex-1 p-6 overflow-auto">
            {/* Total Balance Card */}
            <div className="bg-gradient-to-r from-[#1a1a2e] to-[#16213e] rounded-2xl p-6 mb-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-gray-400 text-sm">Общий баланс</p>
                  <p className="text-3xl font-bold">{totalRub.toLocaleString('ru-RU')} ₽</p>
                </div>
                <button className="p-2 bg-white/10 rounded-lg hover:bg-white/20">
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>
              <div className="flex gap-3">
                <button className="flex-1 py-2.5 bg-[#FFD700] hover:bg-[#FFC000] text-[#1a1a2e] rounded-lg font-medium flex items-center justify-center gap-2">
                  <ArrowDownCircle className="w-4 h-4" /> Вывести
                </button>
                <button className="flex-1 py-2.5 bg-white/10 hover:bg-white/20 rounded-lg font-medium flex items-center justify-center gap-2">
                  <ArrowUpCircle className="w-4 h-4" /> Пополнить
                </button>
              </div>
            </div>

            {/* Crypto Balances */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-medium text-gray-900">Мои активы</h3>
                <button className="text-sm text-blue-500 hover:text-blue-600">Скрыть нулевые</button>
              </div>
              <div className="divide-y divide-gray-100">
                {cryptoBalances.map((crypto) => (
                  <div key={crypto.symbol} className="flex items-center justify-between px-5 py-4 hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <CryptoIcon symbol={crypto.symbol} />
                      <div>
                        <div className="font-medium text-gray-900">{crypto.symbol}</div>
                        <div className="text-xs text-gray-500">{crypto.name}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium text-gray-900">{crypto.balance}</div>
                      <div className="text-xs text-gray-500">≈ {parseFloat(crypto.rub).toLocaleString('ru-RU')} ₽</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Transactions */}
            <div className="mt-6 bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <h3 className="font-medium text-gray-900">Последние операции</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {(data.transactions || []).slice(0, 3).map((tx, index) => (
                  <div key={index} className="flex items-center justify-between px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${tx.type === 'deposit' ? 'bg-red-100' : 'bg-green-100'}`}>
                        {tx.type === 'deposit' ? <ArrowDownCircle className="w-4 h-4 text-red-600" /> : <ArrowUpCircle className="w-4 h-4 text-green-600" />}
                      </div>
                      <div>
                        <div className="text-sm font-medium">{tx.type === 'deposit' ? 'Вывод' : 'Пополнение'}</div>
                        <div className="text-xs text-gray-500">{tx.date} {tx.time}</div>
                      </div>
                    </div>
                    <div className={`font-medium ${tx.type === 'deposit' ? 'text-red-600' : 'text-green-600'}`}>
                      {tx.type === 'deposit' ? '-' : '+'}{tx.amount} {tx.currency}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
